import type { WrittenPage } from '../../sections'
import { PDF } from '../ebook-ai-grocery-shopping'

/**
 * Gıda perakendesinde AI e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "fulfilment", "benchmark", "generative AI" gibi yerleşik
 * terimler de öyle. Marka adları olduğu gibi kalıyor.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
export const ebookAiGroceryShopping: WrittenPage = {
  navLabel: 'Gıda perakendesinde AI',
  title: 'Lead the Shopping Season with AI-Powered Strategies',
  description:
    'Walmart, Kroger, Target ve Amazon Fresh sezonun temposunu şimdiden belirliyor. Gıda perakendesinin liderleri dijital yatırımı nasıl ölçülebilir performansa çeviriyor? Digitopia Impact Labs’in e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Perakende liderleri sezona nasıl hazırlanıyor?',
      headline: 'Lead the Shopping Season with AI-Powered Strategies',
      body: [
        'Walmart, Kroger, Target ve Amazon Fresh sezonun temposunu şimdiden belirliyor.',
        'Gıda perakendesinin liderlerinin dijital yatırımlarını nasıl ölçülebilir performansa '
          + 'çevirdiğini keşfedin. Farklı olarak ne yapıyorlar ve siz aradaki farkı nasıl '
          + 'kapatırsınız?',
      ],
      ctas: [{ label: 'İndirin', href: PDF }],
      photo: {
        src: '/photo/ai-grocery-shopping.webp',
        alt:
          'İçinde ananas, muz ve bir şişe meyve suyu olan kırmızı bir alışveriş sepeti; '
          + 'market reyonunun içinden, alçaktan çekilmiş, arkadaki raflar bulanık.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neden indirmelisiniz?',
      body: [
        'Alışveriş sezonu, gıda perakendecileri için yılı yapar ya da bozar. Ama kazananı artık '
          + 'tek başına promosyonlar ve raf alanı belirlemiyor.',
        'Bugün başarı; AI destekli execution’a, dijital hazırlığa ve sahadaki ekiplerin '
          + 'güçlendirilmesine bağlı. Liderler, diğerlerinin hâlâ denediği şeyi çoktan işin '
          + 'içine almış durumda. Stratejinin doğrudan execution’a döndüğü yeni bir dönemdeyiz.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: PDF }],
      photo: {
        src: '/photo/ai-grocery-shopping-ebook-covers.webp',
        alt:
          'Gıda perakendesi e-kitabının üç kopyası, biri diğerinin arkasında, beyaz zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      sub: 'Bu kısa analizin içinde şunları bulacaksınız:',
      points: [
        'AI pilotları artık ölçekte çalışırken denemenin yerini neden uygulamanın aldığı',
        'Sahadaki ekiplerin gerçek zamanlı karar alabilmek için dijital olarak nasıl '
          + 'güçlendirildiği',
        'Stok, promosyon ve sadakat stratejilerinin neden birlikte çalışması gerektiği',
        'Liderlerin sezonluk talep sıçramalarını nasıl ölçülebilir performansa çevirdiği',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: PDF }],
      photo: {
        src: '/photo/ai-grocery-shopping-ebook-cover.webp',
        alt:
          'Gıda perakendesi e-kitabı ayakta duruyor; kapağı siyah ve pembe bir zemin önünde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Öne çıkanlar',
      sub: 'Raporda şunları göreceksiniz:',
      points: [
        'Walmart mağazalarını nasıl birer fulfilment merkezine dönüştürüyor',
        'Kroger operasyonunu keskinleştirmek için AI ve cloud insight’larını nasıl kullanıyor',
        'Target generative AI’ı mağaza ekiplerine nasıl yayıyor',
        'Amazon Fresh sadakati Prime’a bağlı veriyle nasıl yeniden tanımlıyor',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: PDF }],
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Stratejinizi gıda perakendesinin AI liderleriyle benchmark edin',
      sub:
        'Öndeki gıda perakendecileri alışveriş sezonuna nasıl hazırlanıyor, öğrenin ve kendi '
        + 'yaklaşımınızı benchmark edin.',
      cta: { label: 'E-kitabı indirin', href: PDF },
    },
    {
      kind: 'prose',
      align: 'center',
      paragraphs: [
        'Bu rapor yalnızca bilgilendirme ve benchmark amaçlıdır. Digitopia’nın adı geçen '
        + 'markalarla bir bağı yoktur ve bu markalar tarafından desteklenmemektedir.',
      ],
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'A101’de son 2 yıldır dijital dönüşümle meşgulüz; müşteri deneyiminin farklı '
            + 'boyutlarına, operasyonel iyileştirmelere, inovasyona ve dijital kültüre '
            + 'odaklanıyoruz. Dijitalleşme günlük hayatın her alanında yaşanıyor. Biz de buna '
            + 'uyum sağlamak ve ülke dinamiklerini, perakende sektörünün dinamiklerini ve '
            + 'kurum kültürümüzü gözeten bir yol haritası kurmak istiyoruz.',
          source: 'Orhan Dağlıoğlugil, CTO',
          company: 'A101',
          logo: { name: 'A101', src: '/logo/a101card.png' },
          portrait: { src: '/photo/portrait-orhan-daglioglugil.png', alt: 'Orhan Dağlıoğlugil' },
        },
      ],
    },
  ],
}
